import React from 'react';
import { LoadingSpinner, EmptyState, Badge } from '../ui';
import { gaps } from '../../utils/designTokens';

/**
 * AutoPromptsTab - Admin panel for managing auto-scraped prompts
 * Extracted from monolithic Admin.js
 */
const AutoPromptsTab = ({
  autoPrompts,
  loading,
  filters,
  setFilters,
  page,
  setPage,
  limit,
  setLimit,
  total,
  selection,
  setSelection,
  details,
  setDetails,
  onPublish,
  onDelete,
  onApproveClaim,
  onBulkPublish,
  onBulkDelete,
  onRefresh,
}) => {
  const toggleSelectAllUnposted = () => {
    const next = { ...selection };
    const anyUnselected = autoPrompts.some(ap => !ap.is_posted && !next[ap.id]);
    autoPrompts.forEach(ap => {
      if (!ap.is_posted) next[ap.id] = anyUnselected;
    });
    setSelection(next);
  };

  const selectProcessedUnposted = () => {
    const next = { ...selection };
    const anyUnselected = autoPrompts.some(
      ap => ap.processing_status === 'processed' && !ap.is_posted && !next[ap.id]
    );
    autoPrompts.forEach(ap => {
      if (ap.processing_status === 'processed' && !ap.is_posted) next[ap.id] = anyUnselected;
    });
    setSelection(next);
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading auto prompts..." />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Scraped Auto Prompts</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          These prompts were automatically scraped from RSS feeds and public sources. Review and publish the good ones!
        </p>
      </div>

      {/* Filters */}
      <div className={`flex flex-wrap ${gaps.default} items-end`}>
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Status</label>
          <select
            className="border dark:border-spooky-purple-mist rounded-lg px-3 py-2 text-sm dark:bg-spooky-bg-medium dark:text-white"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="processed">Processed</option>
            <option value="posted">Published</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Min Quality</label>
          <input
            className="border dark:border-spooky-purple-mist rounded-lg px-3 py-2 w-24 text-sm dark:bg-spooky-bg-medium dark:text-white"
            type="number"
            min="0"
            max="100"
            value={filters.minQuality}
            onChange={(e) => setFilters({ ...filters, minQuality: e.target.value })}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            id="hidePublished"
            type="checkbox"
            className="h-4 w-4 rounded"
            checked={filters.hidePublished}
            onChange={(e) => setFilters({ ...filters, hidePublished: e.target.checked })}
          />
          <label htmlFor="hidePublished" className="text-sm dark:text-white">
            Hide published
          </label>
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Search</label>
          <input
            className="border dark:border-spooky-purple-mist rounded-lg px-3 py-2 text-sm dark:bg-spooky-bg-medium dark:text-white"
            placeholder="title/author/source"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Sort</label>
          <div className="flex gap-2">
            <select
              className="border dark:border-spooky-purple-mist rounded-lg px-3 py-2 text-sm dark:bg-spooky-bg-medium dark:text-white"
              value={filters.sortBy}
              onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
            >
              <option value="created_at">Created</option>
              <option value="processed_at">Processed</option>
              <option value="quality_score">Quality</option>
              <option value="source_platform">Source</option>
            </select>
            <select
              className="border dark:border-spooky-purple-mist rounded-lg px-3 py-2 text-sm dark:bg-spooky-bg-medium dark:text-white"
              value={filters.sortOrder}
              onChange={(e) => setFilters({ ...filters, sortOrder: e.target.value })}
            >
              <option value="DESC">Desc</option>
              <option value="ASC">Asc</option>
            </select>
          </div>
        </div>

        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={onRefresh} className="px-3 py-2 border dark:border-spooky-purple-mist rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-spooky-bg-light dark:text-white">
            Refresh
          </button>
          <button onClick={toggleSelectAllUnposted} className="px-3 py-2 border dark:border-spooky-purple-mist rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-spooky-bg-light dark:text-white">
            Select unposted
          </button>
          <button onClick={selectProcessedUnposted} className="px-3 py-2 border dark:border-spooky-purple-mist rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-spooky-bg-light dark:text-white">
            Select processed
          </button>
          <button onClick={onBulkPublish} className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700">
            Bulk publish
          </button>
          <button onClick={onBulkDelete} className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
            Bulk delete
          </button>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage(Math.max(0, page - 1))}
            className="px-3 py-1 border dark:border-spooky-purple-mist rounded-lg disabled:opacity-50 dark:text-white"
          >
            Prev
          </button>
          <button
            disabled={(page + 1) * limit >= total}
            onClick={() => setPage(page + 1)}
            className="px-3 py-1 border dark:border-spooky-purple-mist rounded-lg disabled:opacity-50 dark:text-white"
          >
            Next
          </button>
        </div>
        <div className="dark:text-white">
          Page {total ? page + 1 : 0} of {total ? Math.ceil(total / limit) : 0}
        </div>
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400 mr-2">Per page</label>
          <select
            className="border dark:border-spooky-purple-mist rounded-lg px-2 py-1 text-sm dark:bg-spooky-bg-medium dark:text-white"
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value) || 50);
              setPage(0);
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
        <div className="ml-auto text-gray-500 dark:text-gray-400">Total: {total}</div>
      </div>

      {/* Table */}
      {autoPrompts.length === 0 ? (
        <EmptyState
          title="No auto prompts found"
          message="Run the scraper to collect prompts from external sources!"
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border dark:border-spooky-purple-mist">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-spooky-bg-light">
              <tr className="border-b dark:border-spooky-purple-mist">
                <th className="py-3 px-4">Sel</th>
                <th className="py-3 px-4">Title</th>
                <th className="py-3 px-4">Source</th>
                <th className="py-3 px-4">Quality</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Created</th>
                <th className="py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-spooky-purple-mist">
              {autoPrompts.map((ap) => (
                <tr
                  key={ap.id}
                  className="hover:bg-gray-50 dark:hover:bg-spooky-bg-light dark:text-white"
                >
                  <td className="py-3 px-4">
                    <input
                      type="checkbox"
                      disabled={ap.is_posted}
                      checked={!!selection[ap.id]}
                      onChange={(e) => setSelection({ ...selection, [ap.id]: e.target.checked })}
                      className="rounded"
                    />
                  </td>
                  <td className="py-3 px-4 max-w-md">
                    <div className="font-semibold truncate">{ap.title}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {ap.prompt_text?.substring(0, 100)}...
                    </div>
                    {ap.source_url && (
                      <a
                        href={ap.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-boop-primary hover:underline"
                      >
                        View original
                      </a>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-sm font-medium">{ap.source_platform || 'unknown'}</div>
                    {ap.source_author && (
                      <div className="text-xs text-gray-500">@{ap.source_author}</div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {ap.quality_score !== null && ap.quality_score !== undefined ? (
                      <Badge
                        variant={
                          ap.quality_score >= 85
                            ? 'success'
                            : ap.quality_score >= 70
                            ? 'warning'
                            : 'danger'
                        }
                      >
                        {ap.quality_score}/100
                      </Badge>
                    ) : (
                      <span className="text-gray-400 text-xs">Not scored</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {ap.is_posted ? (
                      <Badge variant="success">Published</Badge>
                    ) : (
                      <Badge variant="info">{ap.processing_status || 'pending'}</Badge>
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs text-gray-500">
                    {new Date(ap.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      {!ap.is_posted && (
                        <>
                          <button
                            onClick={() => onPublish(ap.id)}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700"
                          >
                            Publish
                          </button>
                          {(ap.claim_status === 'auto_claimed' ||
                            ap.claim_status === 'pending_review') && (
                            <button
                              onClick={() => onApproveClaim(ap.id)}
                              className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                            >
                              Approve
                            </button>
                          )}
                          <button
                            onClick={() => onDelete(ap.id)}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setDetails(ap)}
                        className="px-3 py-1 border dark:border-spooky-purple-mist text-xs rounded-lg hover:bg-gray-50 dark:hover:bg-spooky-bg-light"
                      >
                        Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Details Drawer */}
      {details && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDetails(null)}
          />
          <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white dark:bg-spooky-bg-medium shadow-xl p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold dark:text-white">Auto Prompt Details</h3>
              <button
                onClick={() => setDetails(null)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white"
              >
                Close
              </button>
            </div>
            <div className="space-y-4 text-sm dark:text-white">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Title</div>
                <div className="font-medium">{details.title}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Prompt Text</div>
                <pre className="whitespace-pre-wrap bg-gray-50 dark:bg-spooky-bg-light p-3 rounded max-h-64 overflow-auto text-xs">
                  {details.prompt_text}
                </pre>
              </div>
              {details.source_url && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Source</div>
                  <a
                    className="text-boop-primary hover:underline"
                    href={details.source_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {details.source_url}
                  </a>
                </div>
              )}
            </div>
            <div className="mt-6 flex gap-2">
              {!details.is_posted && (
                <>
                  <button
                    onClick={() => onPublish(details.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Publish
                  </button>
                  <button
                    onClick={() => onDelete(details.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Delete
                  </button>
                </>
              )}
              <button
                onClick={() => setDetails(null)}
                className="ml-auto px-4 py-2 border dark:border-spooky-purple-mist rounded-lg hover:bg-gray-50 dark:hover:bg-spooky-bg-light"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoPromptsTab;
